import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

import { get, put } from "lib/api";
import { useAuth } from "context/AuthContext";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosList, IosSectionHeader, IosPage, iosColors } from "mobile/components/IosParity";

const normalize = (d) => (d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d?.documentId || d?.id });
const splitTags = (raw) => {
  if (Array.isArray(raw)) return raw.map((tag) => String(tag || "").trim()).filter(Boolean);
  return String(raw || "")
    .replaceAll("[", "")
    .replaceAll("]", "")
    .replaceAll("\"", "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
};
const rowValue = (item, key, fallback = "") => String(item?.[key] ?? fallback);
const makeTargets = (items) =>
  items.flatMap((item, itemIndex) => {
    const sku = rowValue(item, "sku", "Item");
    const name = rowValue(item, "name", "");
    const quantity = rowValue(item, "quantity", "1");
    const baseKey = rowValue(item, "salesOrderItemId", `${itemIndex}`);
    const tags = splitTags(item.tags);
    if (!tags.length) return [{ id: `${baseKey}|line`, itemIndex, sku, name, label: `Qty ${quantity}`, kind: "Bulk line" }];
    return tags.map((tag) => ({ id: `${baseKey}|tag|${tag}`, itemIndex, sku, name, label: tag, kind: "Tag" }));
  });
const checkedKeysFromItems = (items) => new Set(items.flatMap((item) => splitTags(item.checkedKeys)));

export default function PullOrderWorkflowMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [checkedKeys, setCheckedKeys] = useState(new Set());
  const [pulledBy, setPulledBy] = useState("");
  const [checkedBy, setCheckedBy] = useState("");
  const [notice, setNotice] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-pull-order", documentId],
    queryFn: () =>
      get(`/api/pull-orders/${documentId}`, {
        populate: { salesOrder: { populate: { customer: true } } },
      }),
    enabled: !!documentId,
    staleTime: 5_000,
  });

  const usersQuery = useQuery({
    queryKey: ["m-mobile-dashboard-users"],
    queryFn: () => get("/api/mobile-dashboard/users"),
    staleTime: 60_000,
  });

  const pull = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);
  const salesOrder = useMemo(() => {
    const raw = pull?.salesOrder?.data || pull?.salesOrder || null;
    return raw ? normalize(raw) : null;
  }, [pull]);
  const customer = useMemo(() => {
    const raw = salesOrder?.customer?.data || salesOrder?.customer || null;
    return raw ? normalize(raw) : null;
  }, [salesOrder]);
  const items = useMemo(() => (Array.isArray(pull?.items) ? pull.items : []), [pull]);
  const targets = useMemo(() => makeTargets(items), [items]);
  const groups = useMemo(() => {
    const map = new Map();
    targets.forEach((target) => {
      if (!map.has(target.sku)) map.set(target.sku, { sku: target.sku, name: target.name, targets: [] });
      map.get(target.sku).targets.push(target);
    });
    return Array.from(map.values());
  }, [targets]);
  const users = useMemo(() => (usersQuery.data?.data || []).filter((row) => !["admin", "master", "customer", "distributor"].includes(String(row.appRole || "").toLowerCase())), [usersQuery.data]);
  const complete = targets.length > 0 && checkedKeys.size === targets.length;
  const currentUserLabel = useMemo(() => user?.displayName || user?.username || user?.email || "Current user", [user]);

  useEffect(() => {
    if (!pull) return;
    setPulledBy(pull.pulledBy || currentUserLabel);
    setCheckedBy(pull.checkedBy || "");
    const saved = checkedKeysFromItems(items);
    const status = String(pull.status || "").toLowerCase();
    setCheckedKeys(status === "checked" || status === "completed" ? new Set(targets.map((target) => target.id)) : saved);
  }, [pull, items, targets, currentUserLabel]);

  const payloadItems = () =>
    items.map((item, index) => ({
      ...item,
      checkedKeys: targets
        .filter((target) => target.itemIndex === index && checkedKeys.has(target.id))
        .map((target) => target.id)
        .join(", "),
    }));

  const saveMutation = useMutation({
    mutationFn: ({ finish = false } = {}) =>
      put(`/api/pull-orders/${pull?.documentId || documentId}`, {
        data: {
          status: finish ? "completed" : complete ? "checked" : "pulled",
          pulledBy: pulledBy.trim() || currentUserLabel,
          checkedBy: checkedBy.trim() || null,
          pulledAt: pull?.pulledAt || new Date().toISOString(),
          checkedAt: checkedBy.trim() || finish || complete ? new Date().toISOString() : pull?.checkedAt || null,
          items: payloadItems(),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-pull-order", documentId] });
      qc.invalidateQueries({ queryKey: ["m-pull-queue"] });
      setNotice("Saved.");
    },
    onError: (err) => setNotice(err?.response?.data?.error?.message || err?.message || "Unable to save pull order."),
  });

  const toggleTarget = (targetId) => {
    setNotice("");
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(targetId)) next.delete(targetId);
      else next.add(targetId);
      return next;
    });
  };

  if (isLoading) return <LinearProgress />;
  if (error || !pull) {
    return (
      <IosPage>
        <MobileTopBar title="Pull Order" onBack={() => navigate("/m/pull-orders")} />
        <Typography sx={{ color: iosColors.red, fontWeight: 850, mt: 2 }}>Unable to load pull order.</Typography>
      </IosPage>
    );
  }

  return (
    <IosPage>
      <MobileTopBar
        title={pull.pullOrderNumber || "Pull Order"}
        subtitle={salesOrder?.salesOrderNumber || "Warehouse"}
        onBack={() => navigate("/m/pull-orders")}
      />
      <Stack spacing={1.05} sx={{ mt: 1, pb: 3 }}>
        <IosSectionHeader eyebrow="Warehouse" title="Pull workflow" subtitle="Check every required tag before completing the request." />

        <IosCard>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label={pull.status || "pending"} sx={{ fontWeight: 850 }} />
              <Chip size="small" label={`${checkedKeys.size}/${targets.length} checked`} color={complete ? "success" : "default"} sx={{ fontWeight: 850 }} />
            </Stack>
            <InfoRow label="Customer" value={customer?.name || "-"} />
            <InfoRow label="Sales order" value={salesOrder?.salesOrderNumber || "-"} />
            <InfoRow label="Pulled by" value={pull.pulledBy || "Not signed"} />
            <InfoRow label="Checked by" value={pull.checkedBy || "Not signed"} />
          </Stack>
        </IosCard>

        <Box>
          <Typography sx={{ color: iosColors.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", mb: 0.55, px: 0.25 }}>
            Items To Pull
          </Typography>
          <IosList>
            {groups.map((group, index) => (
              <Box key={group.sku} sx={{ borderBottom: index === groups.length - 1 ? "none" : `1px solid ${iosColors.line}` }}>
                <Box sx={{ px: 1.1, py: 0.95 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: iosColors.ink, fontSize: 15, fontWeight: 900 }} noWrap>
                        {group.sku}
                      </Typography>
                      {group.name ? (
                        <Typography sx={{ color: iosColors.muted, fontSize: 12.25, lineHeight: 1.25 }} noWrap>
                          {group.name}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, flexShrink: 0 }}>
                      {group.targets.filter((target) => checkedKeys.has(target.id)).length}/{group.targets.length}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.55} sx={{ mt: 0.75 }}>
                    {group.targets.map((target) => {
                      const selected = checkedKeys.has(target.id);
                      return (
                        <Box
                          key={target.id}
                          component="button"
                          type="button"
                          onClick={() => toggleTarget(target.id)}
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            textAlign: "left",
                            border: `1px solid ${selected ? "rgba(20,122,92,0.35)" : iosColors.line}`,
                            bgcolor: selected ? "rgba(20,122,92,0.08)" : "#fff",
                            borderRadius: 1,
                            px: 1,
                            py: 0.85,
                          }}
                        >
                          <Icon sx={{ color: selected ? iosColors.green : iosColors.subtle, fontSize: 21 }}>
                            {selected ? "check_circle" : "radio_button_unchecked"}
                          </Icon>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: iosColors.muted, fontSize: 10.5, fontWeight: 900, textTransform: "uppercase" }}>
                              {target.kind}
                            </Typography>
                            <Typography sx={{ color: iosColors.ink, fontSize: 14, fontWeight: 900 }} noWrap>
                              {target.label}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            ))}
            {!groups.length ? (
              <Typography sx={{ color: iosColors.muted, fontSize: 13, p: 1.2 }}>
                No items in this pull request.
              </Typography>
            ) : null}
          </IosList>
        </Box>

        <IosCard>
          <Stack spacing={0.9}>
            <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
              Signoff
            </Typography>
            <TextField label="Pulled by" value={pulledBy} onChange={(e) => setPulledBy(e.target.value)} />
            <TextField select label="Checked by" value={checkedBy} onChange={(e) => setCheckedBy(e.target.value)}>
              <MenuItem value="">Select checker</MenuItem>
              {users.map((row) => (
                <MenuItem key={row.id} value={row.label || row.username || row.email}>
                  {row.label || row.username || row.email}
                </MenuItem>
              ))}
            </TextField>
            <Divider />
            <Stack direction="row" spacing={0.75}>
              <Button variant="outlined" fullWidth disabled={saveMutation.isPending} onClick={() => saveMutation.mutate({ finish: false })}>
                Save
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={saveMutation.isPending || !complete || !pulledBy.trim() || !checkedBy.trim()}
                onClick={() => saveMutation.mutate({ finish: true })}
              >
                Complete
              </Button>
            </Stack>
            {notice ? (
              <Typography sx={{ color: notice === "Saved." ? iosColors.green : iosColors.red, fontSize: 12.5, fontWeight: 850 }}>
                {notice}
              </Typography>
            ) : null}
            {saveMutation.isPending ? <LinearProgress /> : null}
          </Stack>
        </IosCard>
      </Stack>
    </IosPage>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1}>
      <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>{label}</Typography>
      <Typography sx={{ color: iosColors.ink, fontSize: 12.5, fontWeight: 850, textAlign: "right", overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Stack>
  );
}

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};
